import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { UserDataTypes } from 'app/enum/user-data-types';
import { Store } from 'app/model';
import { MidFilter } from 'app/model/mid-filter';
import { MidFilterUpdate } from 'app/model/mid-filter-update';
import { StoreFilters } from 'app/model/seam/store-filters';
import { SeamRequest } from 'app/model/seam-request';
import { UserData } from 'app/model/user-data';
import { createBlankFilterData, FilterData } from 'app/model/user-data/filter-data';
import { storeFiltersService } from 'app/services/store-filters.service';
import { userDataService } from 'app/services/user/user-data.service';
import { AppThunk, RootState } from 'app/store';
import { ModelWithoutId } from 'app/type/model-with-id';

import { setActiveProfile } from '../auth/auth.slice';
import { selectRequest } from '../seam/slice';

interface TupleState {
    activeFilter: FilterData;
    lastRequestId: string;
    savedFilters?: UserData<FilterData[]>;
    storeFilters?: StoreFilters[];
    spendSort?: boolean;
}

const initialState: TupleState = { activeFilter: createBlankFilterData(), lastRequestId: '', spendSort: false };
const defaultFilter: MidFilter = { field: 'merchdesc', value: '', negate: false };

export const TupleSlice = createSlice({
    name: 'tuples',
    initialState,
    reducers: {
        addFilterField: (state) => {
            state.activeFilter.filters.push({ ...defaultFilter });
        },
        removeFilterField: (state, action: PayloadAction<number>) => {
            state.activeFilter.filters.splice(action.payload, 1);
        },
        clearFilter: (state) => {
            state.activeFilter = createBlankFilterData();
        },
        updateFilterField: (state, action: PayloadAction<MidFilterUpdate>) => {
            const payload = action.payload;
            state.activeFilter.filters[payload.key][payload.field] = action.payload.value as never;
        },
        setSavedFilters: (state, action: PayloadAction<UserData<FilterData[]> | undefined>) => {
            state.savedFilters = action.payload;
        },
        setStoreFilters: (state, action: PayloadAction<StoreFilters[] | undefined>) => {
            state.storeFilters = action.payload;
        },
        setActiveFilters: (state, action: PayloadAction<FilterData>) => {
            state.activeFilter = action.payload;
        },
        setSpendSort: (state, action: PayloadAction<boolean>) => {
            state.spendSort = action.payload;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(selectRequest, (state, action: PayloadAction<SeamRequest | undefined>) => {
                if (state.lastRequestId !== action.payload?.id) {
                    state.activeFilter.filters = [];
                }

                state.lastRequestId = action.payload?.id || '';
            })
            .addCase(setActiveProfile, (state) => {
                // if user changes we need to reset loaded filters
                state.savedFilters = undefined;
            });
    },
});

// main reducer.
export const TupleReducers = TupleSlice.reducer;

// selectors
export const selectMidFilters = (state: RootState): FilterData => state.tuples.activeFilter;

export const selectSavedFilters = (state: RootState): UserData<FilterData[]> | undefined => state.tuples.savedFilters;

export const selectStoreFilters = (state: RootState): StoreFilters[] | undefined => state.tuples.storeFilters;

// actions
export const {
    addFilterField,
    removeFilterField,
    updateFilterField,
    clearFilter,
    setSavedFilters,
    setStoreFilters,
    setActiveFilters,
    setSpendSort,
} = TupleSlice.actions;
export const selectSpendSort = (state: RootState): boolean => state.tuples.spendSort!;

// async actions
export const fetchFilters =
    (store?: Store): AppThunk =>
    (dispatch, getState) => {
        const state = getState();
        if (!state.auth.profile) {
            throw new Error('Tried to load filters before user loaded');
        }

        if (store) {
            storeFiltersService.listAll({}, store).subscribe((response) => {
                dispatch(setStoreFilters(response));
            });
        }
        if (!store) {
            dispatch(setStoreFilters([]));
        }

        if (state.tuples.savedFilters) {
            // already loaded
            return;
        }

        userDataService.getFilterData(state.auth.profile).subscribe((filters) => dispatch(setSavedFilters(filters)));
    };

export const saveFilter =
    (changedFilter: FilterData): AppThunk =>
    (dispatch, getState) => {
        const state = getState();

        if (!state.auth.profile) {
            return;
        }

        const savedData: ModelWithoutId<UserData<FilterData[]>> = {
            type: UserDataTypes.FILTERS,
            owner: state.auth.profile,
            ...state.tuples.savedFilters,
            data: [...(state.tuples.savedFilters?.data || [])],
        };

        const found = savedData.data.findIndex((item) => item.name === changedFilter.name);
        if (-1 === found) {
            // new filter
            savedData.data.push(changedFilter);
        } else {
            // upte
            savedData.data[found] = changedFilter;
        }

        userDataService.save(savedData, state.auth.profile).subscribe((filters) => dispatch(setSavedFilters(filters)));

        if (savedData.id) {
            // if we have id dispatch update immediately to provide a more realtime experience
            dispatch(setSavedFilters(savedData as UserData<FilterData[]>));
        }
    };

export const deleteFilter =
    (changedFilter: FilterData): AppThunk =>
    (dispatch, getState) => {
        const state = getState();
        if (!state.tuples.savedFilters) {
            // in proper corse of things we should never end up here
            throw new Error('Filters are not loaded, cannot delete');
        }

        const savedData = {
            ...state.tuples.savedFilters,
            data: state.tuples.savedFilters.data.filter((item) => item.name !== changedFilter.name),
        };

        userDataService.save(savedData, state.auth.profile).subscribe();
        dispatch(setSavedFilters(savedData));
    };

export const saveStoreFilter =
    (filter: FilterData, store: Store): AppThunk =>
    (dispatch, getState) => {
        const state = getState();
        const storeData = selectStoreFilters(state);
        const existingFilter = storeData?.find((f) => f.name === filter.name);

        if (existingFilter) {
            const updatedFilter = {
                id: existingFilter.id,
                name: filter.name,
                filters: filter.filters,
            };
            storeFiltersService.editFilter(updatedFilter, store).subscribe((response) => {
                dispatch(setStoreFilters(storeData?.map((f) => (f.id === response.id ? response : f))));
            });

            return;
        }

        const newFilter = {
            name: filter.name,
            filters: filter.filters,
        };
        storeFiltersService.saveFilter(newFilter, store).subscribe((response) => {
            dispatch(setStoreFilters([...(storeData || []), response]));
        });
    };
